import React, { useEffect, useRef, useState } from "react";
import * as pdfjs from "pdfjs-dist";
import type { PDFDocumentProxy } from "pdfjs-dist";

// The worker file is bundled by webpack from node_modules, not loaded from a CDN.
pdfjs.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.min.js", import.meta.url).toString();

type PageSize = { width: number; height: number };

type PageLink = {
    left: number;
    top: number;
    width: number;
    height: number;
    url?: string;
    dest?: any;
};

/** One page, drawn onto a canvas only when it scrolls near the screen. */
function PdfPage({
    doc,
    pageNumber,
    width,
    size,
    pageRef,
    onGoToPage,
}: {
    doc: PDFDocumentProxy;
    pageNumber: number;
    width: number;
    size: PageSize;
    pageRef: (el: HTMLDivElement | null) => void;
    onGoToPage: (page: number) => void;
}) {
    const holderRef = useRef<HTMLDivElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const [visible, setVisible] = useState(false);
    const [links, setLinks] = useState<PageLink[]>([]);

    useEffect(() => {
        const el = holderRef.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            { rootMargin: "800px 0px" }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!visible || !width) return;
        let cancelled = false;
        let task: { cancel: () => void; promise: Promise<void> } | null = null;
        (async () => {
            const page = await doc.getPage(pageNumber);
            if (cancelled || !canvasRef.current) return;
            const base = page.getViewport({ scale: 1 });
            const ratio = window.devicePixelRatio || 1;
            const scale = width / base.width;
            const viewport = page.getViewport({ scale: scale * ratio });
            const canvas = canvasRef.current;
            canvas.width = Math.floor(viewport.width);
            canvas.height = Math.floor(viewport.height);
            const context = canvas.getContext("2d");
            if (!context) return;
            const renderTask = page.render({ canvasContext: context, viewport });
            task = renderTask;
            try {
                await renderTask.promise;
            } catch {
                /* cancelled while scrolling or resizing */
            }
            if (cancelled) return;

            try {
                const annotations = await page.getAnnotations();
                if (cancelled) return;
                const next: PageLink[] = [];
                for (const ann of annotations || []) {
                    if (ann?.subtype !== "Link" || !Array.isArray(ann.rect) || ann.rect.length < 4) continue;
                    const [x1, y1, x2, y2] = ann.rect;
                    next.push({
                        left: Math.min(x1, x2) * scale,
                        top: (base.height - Math.max(y1, y2)) * scale,
                        width: Math.abs(x2 - x1) * scale,
                        height: Math.abs(y2 - y1) * scale,
                        url: typeof ann.url === "string" ? ann.url : undefined,
                        dest: ann.dest,
                    });
                }
                setLinks(next);
            } catch {
                setLinks([]);
            }
        })();
        return () => {
            cancelled = true;
            task?.cancel();
        };
    }, [doc, pageNumber, visible, width]);

    const height = width ? (size.height / size.width) * width : 0;

    const openLink = async (link: PageLink) => {
        if (link.url) {
            if (/^https?:\/\//i.test(link.url)) {
                window.open(link.url, "_blank", "noopener,noreferrer");
            }
            return;
        }
        if (!link.dest) return;
        try {
            const explicit = typeof link.dest === "string" ? await doc.getDestination(link.dest) : link.dest;
            if (!Array.isArray(explicit) || !explicit[0]) return;
            const index = await doc.getPageIndex(explicit[0]);
            onGoToPage(index + 1);
        } catch {
            /* ignore broken destinations */
        }
    };

    return (
        <div
            ref={(el) => {
                holderRef.current = el;
                pageRef(el);
            }}
            className="relative mx-auto bg-white shadow-md"
            style={{ width, height }}
            data-pdf-page={pageNumber}
        >
            <canvas ref={canvasRef} className="block h-full w-full" aria-label={`Page ${pageNumber}`} />
            {links.map((link, index) => (
                <button
                    key={`${pageNumber}-${index}`}
                    type="button"
                    aria-label={`Go to linked content on page ${pageNumber}`}
                    className="absolute z-[3] cursor-pointer border-0 bg-transparent p-0"
                    style={{
                        left: link.left,
                        top: link.top,
                        width: Math.max(link.width, 12),
                        height: Math.max(link.height, 12),
                    }}
                    onClick={() => {
                        void openLink(link);
                    }}
                />
            ))}
        </div>
    );
}

export default function PdfViewer({ data }: { data: ArrayBuffer }) {
    const wrapRef = useRef<HTMLDivElement | null>(null);
    const pageEls = useRef<Record<number, HTMLDivElement | null>>({});
    const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
    const [sizes, setSizes] = useState<PageSize[]>([]);
    const [width, setWidth] = useState(0);
    const [error, setError] = useState("");

    // Load the document (copy the bytes: pdf.js takes ownership of the buffer it's given).
    useEffect(() => {
        let cancelled = false;
        const task = pdfjs.getDocument({ data: new Uint8Array(data.slice(0)), isEvalSupported: false });
        task.promise
            .then(async (loaded: PDFDocumentProxy) => {
                const all: PageSize[] = [];
                for (let i = 1; i <= loaded.numPages; i += 1) {
                    const vp = (await loaded.getPage(i)).getViewport({ scale: 1 });
                    all.push({ width: vp.width, height: vp.height });
                }
                if (cancelled) return;
                setSizes(all);
                setDoc(loaded);
            })
            .catch(() => !cancelled && setError("This PDF could not be displayed."));
        return () => {
            cancelled = true;
            task.destroy();
        };
    }, [data]);

    // Fit pages to the available width (max 900px), and refit on resize.
    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return;
        const fit = () => setWidth(Math.min(900, Math.max(280, el.clientWidth - 16)));
        fit();
        const observer = new ResizeObserver(fit);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const goToPage = (page: number) => {
        const el = pageEls.current[page];
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <div ref={wrapRef} className="w-full">
            {error ? (
                <p className="py-10 text-center text-sm text-red-600">{error}</p>
            ) : !doc ? (
                <p className="py-10 text-center text-sm text-gray-500">Loading document…</p>
            ) : (
                <div className="flex flex-col gap-4 py-2">
                    {sizes.map((size, i) => (
                        <PdfPage
                            key={i}
                            doc={doc}
                            pageNumber={i + 1}
                            width={width}
                            size={size}
                            pageRef={(el) => {
                                pageEls.current[i + 1] = el;
                            }}
                            onGoToPage={goToPage}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
